import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import TopNav from '../../components/TopNav'
import LoadError from '../../components/LoadError'
import { loadErrorMessage } from '../../utils/loadState'
import { createCustomerAutoId, getPlatformConfig } from '../../api'
import CustomerAvatar from '../../components/CustomerAvatar'
import './NewCustomer.css'

// Fallbacks while loading
const FALLBACK_DOMAINS = [
  { id: 'hcm', label: 'HCM', description: 'HR, Payroll, Benefits, Compensation, Time & Attendance' },
]
const FALLBACK_INDUSTRIES = ['Technology', 'Healthcare', 'Manufacturing', 'Financial Services', 'Other']

// What a failed create tells the user. The server removes a half-made customer before it
// answers, so its message says whether trying again is safe. The one case where it is
// not -- the record could not be removed (half_created) -- names that customer, and the
// form sends the user there to finish its setup instead of creating it a second time.
function createFailure(err) {
  const raw = (err && err.message) || ''
  const m = raw.match(/^API (\d+): ([\s\S]*)$/)
  let detail = null
  if (m) {
    try { detail = JSON.parse(m[2]).detail } catch { detail = m[2] }
  }
  if (detail && typeof detail === 'object') {
    return {
      message: detail.message || 'The customer was not created.',
      halfCreatedId: detail.half_created ? detail.customer_id : null,
    }
  }
  const text = (typeof detail === 'string' && detail) || raw || 'Failed to create customer'
  return { message: `The customer was not created: ${text}`, halfCreatedId: null }
}

// ── Logo fetching utilities ─────────────────────────────────
function cleanDomain(input) {
  if (!input) return ''
  let d = input.trim().toLowerCase()
  d = d.replace(/^https?:\/\//, '').replace(/^www\./, '').split('/')[0]
  return d
}

function getClearbitUrl(domain) {
  return `https://logo.clearbit.com/${domain}`
}

function getFaviconUrl(domain) {
  return `https://www.google.com/s2/favicons?domain=${domain}&sz=128`
}

async function fetchLogoAsBase64(domain) {
  // Try Clearbit first (high-res logo), fall back to Google favicon
  const urls = [getClearbitUrl(domain), getFaviconUrl(domain)]
  for (const url of urls) {
    try {
      const resp = await fetch(url)
      if (resp.ok) {
        const blob = await resp.blob()
        // Only accept images > 1KB (skip tiny fallback icons)
        if (blob.size < 500) continue
        return new Promise((resolve) => {
          const reader = new FileReader()
          reader.onloadend = () => resolve({ base64: reader.result, source: url })
          reader.readAsDataURL(blob)
        })
      }
    } catch { /* try next */ }
  }
  return null
}

export default function NewCustomer() {
  const navigate = useNavigate()
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  // Set when a create failed half way and the half-made customer could not be removed:
  // creating again would make a second customer, so the form points at this one instead.
  const [halfCreatedId, setHalfCreatedId] = useState(null)
  const fileInputRef = useRef(null)
  const [platformConfig, setPlatformConfig] = useState(null)

  const [form, setForm] = useState({
    company_name: '',
    // customers.tenant_kind (migration 104): left empty, the server records 'customer'.
    // Marking a test, demo or proving-ground tenant is an opt-in, off by default.
    tenant_kind: '',
    industry: '',
    employee_count: '',
    website: '',
    domains: [],
    notes: '',
  })

  // Load platform config. A failed read is an error with a Retry: without it the form
  // offers only the fallback lists below (one domain), which used to pass for the
  // platform's full set (item 201).
  const [configError, setConfigError] = useState(null)
  async function loadConfig() {
    setConfigError(null)
    try { setPlatformConfig(await getPlatformConfig()) }
    catch (err) { setConfigError(loadErrorMessage(err)) }
  }
  useEffect(() => { loadConfig() }, [])

  // Derive config values
  const DOMAINS = platformConfig?.domains?.length
    ? platformConfig.domains.map(d => ({ id: d.id, label: d.label, desc: d.description }))
    : FALLBACK_DOMAINS.map(d => ({ id: d.id, label: d.label, desc: d.description }))
  const INDUSTRIES = platformConfig?.industries?.length ? platformConfig.industries : FALLBACK_INDUSTRIES
  // The opt-in kinds: every kind the platform serves other than its default. No fallback
  // list -- if the config cannot load, the customer is created as the default kind.
  const OPT_IN_KINDS = (platformConfig?.tenant_kinds || []).filter(k => !k.default)

  // Logo state
  const [logoPreview, setLogoPreview] = useState(null) // URL for display
  const [logoBase64, setLogoBase64] = useState(null)   // base64 for storage
  const [logoLoading, setLogoLoading] = useState(false)
  const [logoSource, setLogoSource] = useState(null)   // 'clearbit' | 'favicon' | 'upload'
  const [faviconUrl, setFaviconUrl] = useState(null)   // Small favicon for inline display
  const debounceRef = useRef(null)

  // ── Auto-fetch logo when website changes ──────────────────
  const fetchLogo = useCallback(async (domain) => {
    if (!domain || domain.length < 4 || !domain.includes('.')) return

    setLogoLoading(true)
    try {
      const result = await fetchLogoAsBase64(domain)
      if (result) {
        setLogoPreview(result.base64)
        setLogoBase64(result.base64)
        setLogoSource(result.source.includes('clearbit') ? 'clearbit' : 'favicon')
      } else {
        setLogoPreview(null)
        setLogoBase64(null)
        setLogoSource(null)
      }
    } catch {
      setLogoPreview(null)
      setLogoBase64(null)
    }
    // Always try to get favicon for inline display
    setFaviconUrl(getFaviconUrl(domain))
    setLogoLoading(false)
  }, [])

  useEffect(() => {
    const domain = cleanDomain(form.website)
    if (debounceRef.current) clearTimeout(debounceRef.current)

    if (!domain || domain.length < 4 || !domain.includes('.')) {
      setLogoPreview(null)
      setLogoBase64(null)
      setFaviconUrl(null)
      setLogoSource(null)
      return
    }

    debounceRef.current = setTimeout(() => fetchLogo(domain), 600)
    return () => clearTimeout(debounceRef.current)
  }, [form.website, fetchLogo])

  // ── Manual logo upload ────────────────────────────────────
  function handleLogoUpload(e) {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file')
      return
    }
    if (file.size > 2 * 1024 * 1024) {
      setError('Logo must be under 2MB')
      return
    }
    const reader = new FileReader()
    reader.onloadend = () => {
      setLogoPreview(reader.result)
      setLogoBase64(reader.result)
      setLogoSource('upload')
    }
    reader.readAsDataURL(file)
  }

  function clearLogo() {
    setLogoPreview(null)
    setLogoBase64(null)
    setLogoSource(null)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  function updateField(field, value) {
    setForm(prev => ({ ...prev, [field]: value }))
    setError(null)
  }

  function toggleDomain(domainId) {
    setForm(prev => {
      const current = prev.domains
      const updated = current.includes(domainId)
        ? current.filter(d => d !== domainId)
        : [...current, domainId]
      return { ...prev, domains: updated }
    })
    setError(null)
  }

  async function handleSubmit(e) {
    e.preventDefault()

    if (!form.company_name.trim()) {
      setError('Company name is required')
      return
    }
    if (form.domains.length === 0) {
      setError('Select at least one domain')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const systems = form.domains.map((domain, i) => ({
        id: `sys-${i + 1}`,
        domain: domain,
        vendor_id: '',
        vendor_name: '',
        product_id: '',
        product_name: '',
        status: 'planned',
        is_primary: i === 0,
        functions: [],
        connection_id: null,
        connection_status: 'not_connected',
        notes: null,
        replacing_system_id: null,
      }))

      // Item 20 (TENANT IDS ARE COMPANY NAMES): the id is assigned server-side
      // (the next tNNN), never derived from the company name here -- see
      // routers/context.py's derive_next_customer_id.
      const created = await createCustomerAutoId({
        company_name: form.company_name.trim(),
        tenant_kind: form.tenant_kind || undefined,
        industry: form.industry.trim() || undefined,
        employee_count: form.employee_count ? parseInt(form.employee_count, 10) : undefined,
        website: cleanDomain(form.website) || undefined,
        logo_base64: logoBase64 || undefined,
        systems: systems,
        consultant_notes: form.notes.trim() || undefined,
      })
      navigate(`/customers/${created.customer_id}`)
    } catch (err) {
      console.error('Failed to create customer:', err)
      const failure = createFailure(err)
      setError(failure.message)
      setHalfCreatedId(failure.halfCreatedId)
    } finally {
      setSaving(false)
    }
  }

  const breadcrumbs = [
    { label: 'Customers', href: '/customers' },
    { label: 'New Customer' },
  ]

  return (
    <>
      <TopNav breadcrumbs={breadcrumbs} />

      <div className="nc-page">
        <div className="nc-container">

          {/* Live preview card */}
          <div className="nc-preview">
            {/* customerId is cosmetic here (deterministic avatar color only) --
                the real id is assigned server-side on submit, never shown or
                guessed in advance. */}
            <CustomerAvatar customerId="new" companyName={form.company_name || "New"} website={form.website} size="lg" />
            {logoPreview && (
              <div className="nc-preview-logo-wrap">
                <img src={logoPreview} alt="Company logo" className="nc-preview-logo" />
              </div>
            )}
            <div className="nc-preview-info">
              <div className="nc-preview-name">{form.company_name || 'Company Name'}</div>
              <div className="nc-preview-meta">
                {[form.industry, form.employee_count ? `${parseInt(form.employee_count).toLocaleString()} employees` : null].filter(Boolean).join(' · ') || 'Industry · Employees'}
              </div>
              {form.domains.length > 0 && (
                <div className="nc-preview-domains">
                  {form.domains.map(d => {
                    const domain = DOMAINS.find(dom => dom.id === d)
                    return <span key={d} className="nc-preview-domain-tag">{domain?.label || d}</span>
                  })}
                </div>
              )}
            </div>
          </div>

          <form className="nc-form" onSubmit={handleSubmit}>

            {/* Company Name */}
            <div className="nc-field">
              <label className="nc-label">
                Company Name <span className="nc-required">*</span>
              </label>
              <input
                className="nc-input"
                type="text"
                placeholder="e.g. Acme Corporation"
                value={form.company_name}
                onChange={e => updateField('company_name', e.target.value)}
                autoFocus
              />
            </div>

            {/* Company Website + Logo */}
            <div className="nc-field">
              <label className="nc-label">Company Website</label>
              <p className="nc-field-desc">
                Enter the company's website to auto-fetch their logo. Or upload one manually.
              </p>
              <div className="nc-website-row">
                <div className="nc-website-input-wrap">
                  <span className="nc-website-prefix">https://</span>
                  <input
                    className="nc-input nc-website-input"
                    type="text"
                    placeholder="company.com"
                    value={form.website}
                    onChange={e => updateField('website', e.target.value)}
                  />
                  {faviconUrl && form.website && (
                    <img
                      src={faviconUrl}
                      alt=""
                      className="nc-website-favicon"
                      onError={e => { e.target.style.display = 'none' }}
                    />
                  )}
                  {logoLoading && <span className="nc-website-loading">⟳</span>}
                </div>
                <button type="button" className="nc-upload-logo-btn" onClick={() => fileInputRef.current?.click()}>
                  Upload Logo
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: 'none' }}
                  onChange={handleLogoUpload}
                />
              </div>

              {/* Logo result */}
              {logoPreview && (
                <div className="nc-logo-result">
                  <img src={logoPreview} alt="Company logo" className="nc-logo-result-img" />
                  <div className="nc-logo-result-info">
                    <span className="nc-logo-result-source">
                      {logoSource === 'clearbit' ? 'Auto-fetched from Clearbit' :
                       logoSource === 'favicon' ? 'Fetched from website favicon' :
                       'Manually uploaded'}
                    </span>
                    <span className="nc-logo-result-note">Stored for use in exports and reports</span>
                  </div>
                  <button type="button" className="nc-logo-clear" onClick={clearLogo}>✕</button>
                </div>
              )}
            </div>

            {/* Industry + Employee Count */}
            <div className="nc-row">
              <div className="nc-field">
                <label className="nc-label">Industry</label>
                <select
                  className="nc-select"
                  value={form.industry}
                  onChange={e => updateField('industry', e.target.value)}
                >
                  <option value="">Select…</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
              <div className="nc-field">
                <label className="nc-label">Employee Count</label>
                <input
                  className="nc-input"
                  type="number"
                  placeholder="e.g. 2500"
                  value={form.employee_count}
                  onChange={e => updateField('employee_count', e.target.value)}
                />
              </div>
            </div>

            {/* Domain Selection */}
            <div className="nc-field">
              <label className="nc-label">
                Engagement Domains <span className="nc-required">*</span>
              </label>
              <p className="nc-field-desc">What systems are in scope? Select all that apply — you'll configure specific vendors and products on the next page.</p>
              {configError && (
                <LoadError inline what="the platform's domains and industries (only defaults are listed)"
                  error={configError} onRetry={loadConfig} />
              )}
              <div className="nc-domain-grid">
                {DOMAINS.map(domain => {
                  const selected = form.domains.includes(domain.id)
                  return (
                    <button
                      key={domain.id}
                      type="button"
                      className={`nc-domain-card ${selected ? 'selected' : ''}`}
                      onClick={() => toggleDomain(domain.id)}
                    >
                      <div className="nc-domain-check">
                        {selected && <span className="nc-domain-checkmark">✓</span>}
                      </div>
                      <div>
                        <div className="nc-domain-label">{domain.label}</div>
                        <div className="nc-domain-desc">{domain.desc}</div>
                      </div>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Test, demo or proving-ground tenant: an opt-in, off by default */}
            {OPT_IN_KINDS.map(kind => (
              <div className="nc-field" key={kind.id}>
                <label className="nc-label">
                  <input
                    type="checkbox"
                    checked={form.tenant_kind === kind.id}
                    onChange={e => updateField('tenant_kind', e.target.checked ? kind.id : '')}
                  />{' '}
                  {kind.label}
                </label>
                <p className="nc-field-desc">{kind.description} Leave unchecked for a real customer.</p>
              </div>
            ))}

            {/* Notes */}
            <div className="nc-field">
              <label className="nc-label">Notes</label>
              <textarea
                className="nc-textarea"
                placeholder="Any initial context — project type, timeline, key contacts…"
                value={form.notes}
                onChange={e => updateField('notes', e.target.value)}
                rows={3}
              />
            </div>

            {/* Error */}
            {error && (
              <div className="nc-error" role="alert">
                {error}
                {halfCreatedId && (
                  <div className="nc-error-action">
                    <button type="button" className="nc-btn-cancel"
                      onClick={() => navigate(`/customers/${halfCreatedId}`)}>
                      Open {halfCreatedId} to finish its setup →
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="nc-actions">
              <button type="button" className="nc-btn-cancel" onClick={() => navigate('/customers')}>
                Cancel
              </button>
              <button type="submit" className="nc-btn-submit" disabled={saving || !!halfCreatedId}
                title={halfCreatedId ? `Finish setting up ${halfCreatedId} instead of creating it again` : undefined}>
                {saving ? 'Creating…' : 'Create Customer →'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}

